import Image from "next/image";

import type { FragmentType } from "../../../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../../../__generated__/gql";
import { ExternalSiteIcon, MapIcon } from "../../../../../_components/dx-icons";
import { DxIconButton } from "../../../_components/dx-icon-button";
import { DxImagePlaceholder } from "../../../_components/dx-image-placeholder";
import { buildSpotCategoryLabel } from "../_utils/spot-category";

export const SpotCardFragment = graphql(`
  fragment SpotCard_AreaGuideSpot on AreaGuideSpot {
    id
    name
    categories
    tags
    description
    businessHours
    websiteUrl
    mapUrl
    images {
      url
    }
  }
`);

export function SpotCard({
  spot: spotData,
}: {
  spot: FragmentType<typeof SpotCardFragment>;
}) {
  const spot = useFragment(SpotCardFragment, spotData);
  const categoryLabel = buildSpotCategoryLabel(spot.categories, spot.tags);
  const imageUrl = spot.images[0]?.url ?? null;
  const websiteUrl = spot.websiteUrl ?? null;
  const mapUrl = spot.mapUrl ?? null;
  const description = spot.description ?? null;
  const businessHours = spot.businessHours ?? null;
  return (
    <article className="bg-dx-surface-base flex w-full flex-col gap-[8px] py-[16px]">
      <div className="px-[16px]">
        {imageUrl !== null ? (
          <div className="bg-dx-surface-subtle relative aspect-[16/9] w-full overflow-hidden">
            <Image
              src={imageUrl}
              alt={spot.name}
              fill
              className="object-cover"
              sizes="(min-width: 1024px) 288px, 100vw"
            />
          </div>
        ) : (
          <DxImagePlaceholder aspectRatio="16/9" label="Spot" />
        )}
      </div>
      <div className="flex flex-col pt-[4px] pr-[8px] pl-[16px]">
        <div className="flex items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
            <p className="dx-subhead text-dx-text-default">{spot.name}</p>
            {categoryLabel !== "" && (
              <p className="dx-body-3 text-dx-text-sub">{categoryLabel}</p>
            )}
          </div>
          <div className="flex flex-none items-center">
            {websiteUrl !== null && (
              <DxIconButton
                variant="transparent"
                size="small"
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                ariaLabel="公式サイトを別タブで開く"
                data-analytics-id="area_spots_clicked_spot_website"
                icon={<ExternalSiteIcon />}
              />
            )}
            {mapUrl !== null && (
              <DxIconButton
                variant="transparent"
                size="small"
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                ariaLabel="Google Maps で開く"
                data-analytics-id="area_spots_clicked_spot_map"
                icon={<MapIcon />}
              />
            )}
          </div>
        </div>
        {(description !== null || businessHours !== null) && (
          <div className="flex flex-col gap-[4px] pt-[8px] pr-[8px]">
            {description !== null && (
              <p className="dx-body-2 text-dx-text-default whitespace-pre-line">
                {description}
              </p>
            )}
            {businessHours !== null && (
              <p className="dx-body-3 text-dx-text-sub">{businessHours}</p>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
